import React, { useEffect, useState, useCallback } from 'react';
import {
    CircularProgress,
    Button,
    Chip,
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Paper,
    Avatar,
    useTheme,
    alpha,
    Grid,
    Skeleton,
    Fade,
    IconButton,
    Tooltip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CalendarToday as CalendarIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    TrendingUp as TrendingUpIcon,
    Schedule as ScheduleIcon,
    Analytics as AnalyticsIcon,
    Code as CodeIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { getRatingData, deleteUserRating, getUserRatingResults } from '../../Queries';
import RatingPopup from './RatingPopup';
import RatingHistoryDialog from '../RatingHistoryDialog';

const RatingFormsForUser = () => {
    const theme = useTheme();
    const [formsData, setFormsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedForm, setSelectedForm] = useState(null);
    const [openPopup, setOpenPopup] = useState(false);
    const [formStatus, setFormStatus] = useState('');
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [userBatch, setUserBatch] = useState('');
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const currentUser = localStorage.getItem("user");// RatingFormsForUser.js - Enhanced Component with Custom Table UI

    const getStatus = useCallback((startDate, endDate) => {
        const currentDate = new Date();
        const formattedStartDate = new Date(startDate);
        const formattedEndDate = new Date(endDate);
        
        if (currentDate < formattedStartDate) {
            return 'Upcoming';
        } else if (currentDate >= formattedStartDate && currentDate <= formattedEndDate) {
            return 'Active';
        } else {
            return 'Closed';
        }
    }, []);

    const formatDate = useCallback((dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    }, []);

    const getFormsForSelectedUser = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const forms = await getRatingData();
            
            if (!userBatch) {
                console.log('No userBatch available, showing all forms for debugging');
                setFormsData(forms || []);
                return;
            }
            
            // Filter forms based on user's batch matching with program_code
            const filteredForms = (forms || []).filter(form => {
                const matches = form.program_code === userBatch;
                return matches;
            });
            
            // Get user ratings to show completion status
            const userRatings = await getUserRatingResults(currentUser);
            // Add completion status to forms
            const formsWithStatus = filteredForms.map(form => {
                // Check if entry exists based on user_id and parent_id (form id)
                const userRating = userRatings.userRatingResults.find(rating => {
                    // Handle DynamoDB format with .S attributes
                    const ratingUserId = rating.user_id?.S || rating.user_id;
                    const ratingParentId = rating.parent_id?.S || rating.parent_id;
                    return ratingUserId === currentUser && ratingParentId === form.form_id.toString();
                });
                
                return {
                    ...form,
                    hasUserRating: !!userRating,
                    userRatingId: userRating?.id?.S || userRating?.id,
                    isCompleted: !!userRating, // If entry exists, consider it completed
                    userRatingData: userRating
                };
            });

            setFormsData(formsWithStatus);
        } catch (error) {
            console.error('Error fetching forms data:', error);
            setError('Failed to load self-reflections. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [userBatch, currentUser]);

    // Get user's batch from localStorage userData
    useEffect(() => {
        try {
            const userData = localStorage.getItem("userData");
            
            if (userData) {
                const parsedUserData = JSON.parse(userData);                
                const batch = parsedUserData.user_batch;
                                setUserBatch(batch);
            } else {
                console.log('No userData found in localStorage');
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error parsing userData from localStorage:', error);
            setIsLoading(false);
        }
    }, []);

    // Fetch forms when userBatch changes
    useEffect(() => {
        if (userBatch) {
            console.log('UserBatch found, fetching forms for batch:', userBatch);
        } else {
            console.log('UserBatch is empty, fetching all forms for debugging');
        }
        getFormsForSelectedUser();
    }, [userBatch, getFormsForSelectedUser]);

    const handlePreviewClick = useCallback((selectedForm, isEdit = false) => {
        try {
            setFormStatus(getStatus(selectedForm.start_date, selectedForm.end_date));

            // Check if questions is already parsed or needs parsing
            let parsedQuestions;
            if (typeof selectedForm.questions === 'string') {
                parsedQuestions = JSON.parse(selectedForm.questions);
            } else {
                parsedQuestions = selectedForm.questions;
            }

            // Create a copy to avoid mutating the original data
            const formData = {
                ...selectedForm,
                questions: parsedQuestions,
                isEditMode: isEdit
            };

            setSelectedForm({ row: formData });
            setOpenPopup(true);
        } catch (error) {
            console.error('Error opening preview:', error);
            setError('Failed to open evaluation preview.');
        }
    }, [getStatus]);

    const handleClosePopup = useCallback(() => {
        setOpenPopup(false);
        setSelectedForm(null);
        // Refresh the table data when popup closes
        getFormsForSelectedUser();
    }, [getFormsForSelectedUser]);

    const handleCloseError = useCallback(() => {
        setError(null);
    }, []);

    const handleHistoryClick = useCallback(() => {
        // Create user object from current user data
        const userData = localStorage.getItem("userData");
        let user = { email: currentUser, id: currentUser };
        
        if (userData) {
            try {
                const parsedUserData = JSON.parse(userData);
                user = {
                    email: currentUser,
                    id: currentUser,
                    name: parsedUserData.name || currentUser
                };
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
        
        setHistoryDialogOpen(true);
    }, [currentUser]);

    const handleHistoryClose = useCallback(() => {
        setHistoryDialogOpen(false);
    }, []);

    const handleDeleteRating = useCallback(async (formId) => {
        try {
            setIsLoading(true);
            
            // Get user's rating for this form
            const userRatings = await getUserRatingResults(currentUser);
            const userRating = userRatings.find(rating => {
                const ratingUserId = rating.user_id?.S || rating.user_id;
                const ratingParentId = rating.parent_id?.S || rating.parent_id;
                return ratingUserId === currentUser && ratingParentId === formId.toString();
            });
            
            if (userRating) {
                const ratingId = userRating.id?.S || userRating.id;
                const response = await deleteUserRating(ratingId, currentUser);
                
                if (response.data && response.data.statusCode === 200) {
                    // Refresh the forms list
                    await getFormsForSelectedUser();
                    setError(null);
                } else {
                    setError('Failed to delete rating. Please try again.');
                }
            } else {
                setError('No rating found to delete.');
            }
        } catch (error) {
            console.error('Error deleting rating:', error);
            setError('Failed to delete rating. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [currentUser, getFormsForSelectedUser]);

    const canDeleteRating = useCallback((formStartDate, formEndDate) => {
        // Allow deletion only if form is still active
        return getStatus(formStartDate, formEndDate) === 'Active';
    }, [getStatus]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Enhanced loading component
    const LoadingComponent = () => (
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <CardContent sx={{ p: 4 }}>
                <Stack spacing={3}>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                        <CircularProgress size={32} thickness={4} />
                        <Typography variant="h6" color="text.secondary">
                            Loading your evaluations...
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    // Enhanced empty state component
    const EmptyStateComponent = () => (
        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <Avatar
                    sx={{
                        width: 80,
                        height: 80,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mx: 'auto',
                        mb: 3
                    }}
                >
                    <AssignmentIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Self-Reflections Available
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 400, mx: 'auto' }}>
                    You don't have any performance self-reflections assigned at the moment.
                    Your mentor will create self-reflections for you to complete.
                </Typography>
            </CardContent>
        </Card>
    );

    // Statistics summary component
    const StatsSummary = () => {
        const totalForms = formsData.length;
        const completedSelfReflections = formsData.filter(item => item.isCompleted).length;
        const activeSelfReflections = formsData.filter(item => getStatus(item.start_date, item.end_date) === 'Active' && !item.isCompleted).length;
        const upcomingSelfReflections = formsData.filter(item => getStatus(item.start_date, item.end_date) === 'Upcoming').length;
        const closedSelfReflections = formsData.filter(item => getStatus(item.start_date, item.end_date) === 'Closed').length;

        return (
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${alpha('#0a0068', 0.05)} 0%, ${alpha('#0c0082', 0.02)} 100%)`
                        }}
                    >
                        <Avatar sx={{
                            bgcolor: alpha('#0a0068', 0.1),
                            color: '#0a0068',
                            mx: 'auto',
                            mb: 1,
                            width: 48,
                            height: 48
                        }}>
                            <AnalyticsIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            {totalForms}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Assigned
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: `1px solid ${alpha('#2196F3', 0.2)}`,
                            borderRadius: 2,
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${alpha('#2196F3', 0.05)} 0%, ${alpha('#2196F3', 0.02)} 100%)`
                        }}
                    >
                        <Avatar sx={{
                            bgcolor: alpha('#2196F3', 0.1),
                            color: '#1976D2',
                            mx: 'auto',
                            mb: 1,
                            width: 48,
                            height: 48
                        }}>
                            <TrendingUpIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="info.main">
                            {upcomingSelfReflections}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Upcoming
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: `1px solid ${alpha('#ff9800', 0.2)}`,
                            borderRadius: 2,
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${alpha('#ff9800', 0.05)} 0%, ${alpha('#ff9800', 0.02)} 100%)`
                        }}
                    >
                        <Avatar sx={{
                            bgcolor: alpha('#ff9800', 0.1),
                            color: '#E65100',
                            mx: 'auto',
                            mb: 1,
                            width: 48,
                            height: 48
                        }}>
                            <ScheduleIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="warning.main">
                            {activeSelfReflections}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Active
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: `1px solid ${alpha('#4CAF50', 0.2)}`,
                            borderRadius: 2,
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${alpha('#4CAF50', 0.05)} 0%, ${alpha('#4CAF50', 0.02)} 100%)`
                        }}
                    >
                        <Avatar sx={{
                            bgcolor: alpha('#4CAF50', 0.1),
                            color: '#2E7D32',
                            mx: 'auto',
                            mb: 1,
                            width: 48,
                            height: 48
                        }}>
                            <CheckCircleIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                            {completedSelfReflections}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Completed
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            border: `1px solid ${alpha('#f44336', 0.2)}`,
                            borderRadius: 2,
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${alpha('#f44336', 0.05)} 0%, ${alpha('#f44336', 0.02)} 100%)`
                        }}
                    >
                        <Avatar sx={{
                            bgcolor: alpha('#f44336', 0.1),
                            color: '#C62828',
                            mx: 'auto',
                            mb: 1,
                            width: 48,
                            height: 48
                        }}>
                            <CancelIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="error.main">
                            {closedSelfReflections}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Closed
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    // Custom Table Component matching the image design
    const CustomTable = () => {
        const paginatedData = formsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        return (
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: alpha('#f8f9fa', 0.8) }}>
                                <TableCell sx={{ fontWeight: 600, color: '#0a0068', py: 2 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CalendarIcon sx={{ fontSize: 18 }} />
                                        Start Date
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#0a0068', py: 2 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CalendarIcon sx={{ fontSize: 18 }} />
                                        End Date
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#0a0068', py: 2 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <AssignmentIcon sx={{ fontSize: 18 }} />
                                        Self-Reflection Name
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#0a0068', py: 2 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CheckCircleIcon sx={{ fontSize: 18 }} />
                                        Status
                                    </Box>
                                </TableCell>
                                {/* <TableCell sx={{ fontWeight: 600, color: '#0a0068', py: 2 }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <CodeIcon sx={{ fontSize: 18 }} />
                                        Program Code
                                    </Box>
                                </TableCell> */}
                                <TableCell sx={{ fontWeight: 600, color: '#0a0068', py: 2 }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedData.map((row, index) => {
                                const status = getStatus(row.start_date, row.end_date);
                                return (
                                    <TableRow
                                        key={row.id}
                                        sx={{
                                            '&:hover': { backgroundColor: alpha('#0a0068', 0.02) },
                                            '&:nth-of-type(even)': { backgroundColor: alpha('#f8f9fa', 0.3) }
                                        }}
                                    >
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" fontWeight="500">
                                                    {formatDate(row.start_date)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" fontWeight="500">
                                                    {formatDate(row.end_date)}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="600"
                                                    color="primary"
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => handlePreviewClick(row, false)}
                                                >
                                                    {row.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Chip
                                                    icon={
                                                        status === 'Active' ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : 
                                                        status === 'Upcoming' ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : 
                                                        <CancelIcon sx={{ fontSize: 16 }} />
                                                    }
                                                    label={status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 
                                                            status === 'Active' ? alpha('#4CAF50', 0.1) : 
                                                            status === 'Upcoming' ? alpha('#2196F3', 0.1) : 
                                                            alpha('#f44336', 0.1),
                                                        color: 
                                                            status === 'Active' ? '#2E7D32' : 
                                                            status === 'Upcoming' ? '#1976D2' : 
                                                            '#C62828',
                                                        fontWeight: 600,
                                                        border: `1px solid ${
                                                            status === 'Active' ? alpha('#4CAF50', 0.3) : 
                                                            status === 'Upcoming' ? alpha('#2196F3', 0.3) : 
                                                            alpha('#f44336', 0.3)
                                                        }`,
                                                        '& .MuiChip-icon': {
                                                            color: 
                                                                status === 'Active' ? '#2E7D32' : 
                                                                status === 'Upcoming' ? '#1976D2' : 
                                                                '#C62828'
                                                        }
                                                    }}
                                                />
                                                {row.isCompleted && (
                                                    <Chip
                                                        label="Submitted"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha('#9C27B0', 0.1),
                                                            color: '#7B1FA2',
                                                            fontWeight: 600,
                                                            border: `1px solid ${alpha('#9C27B0', 0.3)}`,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        {/* <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CodeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" fontWeight="500">
                                                    {row.program_code}
                                                </Typography>
                                            </Box>
                                        </TableCell> */}
                                        <TableCell sx={{ py: 2 }}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {status === 'Active' ? (
                                                    // Show Edit button only for active forms (during the time period)
                                                    <Tooltip title={row.isCompleted ? "Edit Self-Reflection" : "Complete Self-Reflection"} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handlePreviewClick(row, true)}
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                                '&:hover': {
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : status === 'Closed' ? (
                                                    // Show Preview button only for closed forms
                                                    <Tooltip title="Preview Self-Reflection (Closed)" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handlePreviewClick(row, false)}
                                                            sx={{
                                                                color: theme.palette.info.main,
                                                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                                                                '&:hover': {
                                                                    backgroundColor: alpha(theme.palette.info.main, 0.2),
                                                                }
                                                            }}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    // No button for upcoming forms
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        Available on {formatDate(row.start_date)}
                                                    </Typography>
                                                )}
                                                {/* {canDeleteRating(row.start_date, row.end_date) && row.hasUserRating && (
                                                    <Tooltip title="Delete Rating" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteRating(row.id)}
                                                            sx={{
                                                                color: theme.palette.error.main,
                                                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                                '&:hover': {
                                                                    backgroundColor: alpha(theme.palette.error.main, 0.2),
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )} */}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={formsData.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        '& .MuiTablePagination-toolbar': {
                            paddingRight: 2
                        }
                    }}
                />
            </Card>
        );
    };

    if (isLoading) {
        return (
            <Fade in={true} timeout={600}>
                <Box>
                    <LoadingComponent />
                </Box>
            </Fade>
        );
    }

    if (error) {
        return (
            <Fade in={true} timeout={600}>
                <Box>
                    <Alert
                        severity="error"
                        onClose={handleCloseError}
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-message': {
                                fontSize: '0.9rem'
                            }
                        }}
                    >
                        {error}
                    </Alert>
                </Box>
            </Fade>
        );
    }

    if (formsData.length === 0) {
        return (
            <Fade in={true} timeout={600}>
                <Box>
                    <EmptyStateComponent />
                </Box>
            </Fade>
        );
    }

    return (
        <Fade in={true} timeout={800}>
            <Box>
                {/* Header Section with History Button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#0a0068' }}>
                        My Self-Reflection Forms
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={handleHistoryClick}
                        startIcon={<HistoryIcon />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            borderColor: '#2196f3',
                            color: '#2196f3',
                            '&:hover': {
                                backgroundColor: alpha('#2196f3', 0.05),
                                borderColor: '#2196f3'
                            }
                        }}
                    >
                        View History
                    </Button>
                </Box>

                {/* Statistics Summary */}
                <StatsSummary />

                {/* Custom Table matching the image design */}
                <CustomTable />

                {/* Enhanced Rating Popup */}
                {openPopup && (
                    <RatingPopup
                        open={openPopup}
                        name=""
                        userEmail={currentUser}
                        onClose={handleClosePopup}
                        questions={selectedForm.row.questions}
                        status={formStatus}
                        form={selectedForm.row}
                    />
                )}

                {/* History Dialog */}
                <RatingHistoryDialog
                    open={historyDialogOpen}
                    onClose={handleHistoryClose}
                    selectedUser={{ email: currentUser, id: currentUser }}
                />
            </Box>
        </Fade>
    );
};

export default RatingFormsForUser;